// shared base
export type Option = { label: string; value: string | number };

export interface Cohort {
    cohort_id: string;
    name: string;
    description?: string | null;
    category_id: string;
    category_name?: string;
    filter_criteria?: string | null;
    linked_cohorts?: string[];
    created_at: string;
    updated_at: string;
    patient_count: number;
    document_count: number;
    message_id?: string;
    sql_query?: string;
    conversation_id?: string;
}

export interface CreateCohortDto {
    name: string;
    is_new_category: boolean;
    snomed_code_id?: string;
    omop_concept_id?: string;
    timeframe_months?: number;
    category_id?: string;
    category_name?: string;
    linked_cohorts?: string[];
    patient_ids?: string[];
    patient_filter?: any;
    chat_filter?: string;
    sql_query?: string;
    message_id?: string;
    clinical_trial_job_id?: string;
    random_patients?: number;
    random_seed?: number;
    ingestion_job_ids?: string[]; // Ingestion job IDs for building cohort from ingestion
    ingestion_filter?: any; // Filter criteria for ingestion job mode
}

export interface UpdateCohortDto {
    name?: string;
    snomed_code_id?: string;
    omop_concept_id?: string;
    timeframe_months?: number;
    status?: string;
    category_id?: string;
    // Cohort builder advanced filters (kept as any to match backend AdvancedFilterQuery)
    patient_filter?: any;
    chat_filter?: string;
    sql_query?: string;
    random_patients?: number;
    random_seed?: number;
    /** Silver+Bronze: include Elasticsearch/NLP data matches for diagnosis/procedure filters */
    include_elasticsearch_for_filters?: boolean;
}

export interface CohortResponseDto {
    id: string;
    name: string;
    snomed_code_id?: string;
    omop_concept_id?: string;
    timeframe_months?: number;
    category_id?: string;
    linked_cohorts?: string[];
    created_at: string;
    updated_at: string;
}

export interface CohortResponseListDto {
    cohorts: Cohort[];
    total: number;
    count: number;
}

// ===========================
// UI Props: Create Cohort
// ===========================
export type CreateCohortModalProps = {
    open: boolean;
    defaultName?: string;
    isPending?: boolean;
    onClose: () => void;
    onSave: (payload: CreateCohortDto) => Promise<void> | void;
};

export type CreateCohortButton = {
    height?: number;
};

export interface CohortSuccessModalProps {
    open: boolean;
    onClose: () => void;
    cohortName: string;
    onBackToChat: () => void;
    onGoToCohorts: () => void;
    showBackButton?: boolean;
}

// ===========================
// Cohort Category Types
// ===========================
export interface CohortCategoryDto {
    category_id: string;
    name: string;
    description?: string | null;
    created_at: string;
    updated_at: string;
}

export interface CreateCohortCategoryDto {
    name: string;
    description?: string | null;
}

export interface UpdateCohortCategoryDto {
    name?: string;
    description?: string | null;
}

export interface CohortCategoryResponseDto {
    category_id: string;
    name: string;
    description?: string | null;
    created_at: string;
    updated_at: string;
}

export interface CohortCategoryListResponseDto {
    categories: CohortCategoryDto[];
    total: number;
}

export interface CohortCategoryResponseDto {
    category: CohortCategoryDto;
    total: number;
}

// ===========================
// Cohort Membership Types
// ===========================
export interface CohortMembershipDto {
    membership_id: string;
    cohort_id: string;
    patient_id: string;
    created_at: string;
}

export interface CreateCohortMembershipDto {
    cohort_id: string;
    patient_id: string;
}

export interface CohortMembershipResponseDto {
    membership_id: string;
    cohort_id: string;
    patient_id: string;
    created_at: string;
}

export interface CohortMembershipListResponseDto {
    memberships: CohortMembershipDto[];
    total: number;
    count: number;
}

export interface CohortNamesResponseDto {
    cohorts: {
        id: string | number;
        name: string;
    }[];
}

// ===========================
// Sub-Cohort Types
// ===========================
export interface SubCohortDto {
    subcohort_id: string;
    parent_cohort_id: string;
    name: string;
    description?: string | null;
    created_at: string;
    updated_at: string;
}

export interface CreateSubCohortDto {
    parent_cohort_id: string;
    name: string;
    description?: string | null;
}

export interface UpdateSubCohortDto {
    name?: string;
    description?: string | null;
}

export interface SubCohortResponseDto {
    subcohort_id: string;
    parent_cohort_id: string;
    name: string;
    description?: string | null;
    created_at: string;
    updated_at: string;
}

export interface SubCohortListResponseDto {
    subcohorts: SubCohortDto[];
    total: number;
    count: number;
}
